/**
 * Module dependencies.
 */

import { SubTitle } from 'app/components/atoms/sub-title/sub-title';
import { Field } from 'app/components/organisms/field/field';
import { formatCurrency } from 'app/core/utils/formatter';
import BigNumber from 'bignumber.js';
import { reduce } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Text } from 'app/components/atoms/text/text';
import { useProject } from 'app/hooks/requests/projects/use-project';

/**
 * `Props` type.
 */

type Props = {
  data: any[] | undefined;
};

/**
 * Export `Report` component.
 */

export function Report({ data }: Props): JSX.Element {
  const [translate] = useTranslation();
  const { projectId } = useParams();
  const { data: project } = useProject(projectId);
  const area = project?.areaM2 ? new BigNumber(project.areaM2) : null;
  const { initialPrevision, paid, debt, budgetDeviation } = useMemo(() => {
    return reduce(
      data,
      (acc, item) => {
        acc.initialPrevision = acc.initialPrevision.plus(item.prevision ?? '0');
        acc.paid = !item.paid ? acc.paid : acc.paid.plus(item.paid);
        acc.debt = !item.debt ? acc.debt : acc.debt.plus(item.debt);
        acc.budgetDeviation =
          !item.diffOverBudget || item.proposals.length === 0
            ? acc.budgetDeviation
            : acc.budgetDeviation.plus(item.diffOverBudget);

        // TODO: Bug no budgetDeviation

        return {
          ...acc
        };
      },
      {
        initialPrevision: new BigNumber('0'),
        paid: new BigNumber('0'),
        debt: new BigNumber('0'),
        budgetDeviation: new BigNumber('0')
      }
    );
  }, [data]);

  return (
    <div className={'min-w-[300px] max-w-[300px] relative'}>
      <SubTitle className={'!text-[rgb(8,108,124)] font-bold border-b-2 border-[#ccc] pb-2 mb-2 w-full block'}>
        {translate('common.labels.paid')}
      </SubTitle>

      <Field
        title={translate('common.labels.initialPrevision')}
        value={formatCurrency(new BigNumber(initialPrevision).toFixed(2))}
      />

      <Field title={translate('common.labels.totalPaid')} value={formatCurrency(new BigNumber(paid).toFixed(2))} />

      <Field
        danger={debt.isGreaterThan('0')}
        title={translate('common.labels.needPaid')}
        value={formatCurrency(new BigNumber(debt).toFixed(2))}
      />

      <Field
        danger={budgetDeviation.isLessThan('0')}
        title={translate('common.labels.budgetDeviation')}
        value={formatCurrency(new BigNumber(budgetDeviation).toFixed(2))}
      />

      <Field
        title={translate('common.labels.newPrevision')}
        value={formatCurrency(initialPrevision.minus(new BigNumber(budgetDeviation).toFixed(2)))}
      />

      <Field
        title={translate('common.labels.toComplete')}
        value={formatCurrency(initialPrevision.minus(budgetDeviation).minus(paid).toFixed(2))}
      />

      {!!area && area.isGreaterThan(0) && (
        <>
          <Text className={'!mt-8 block !text-[rgb(8,108,124)] mb-2'}>{'Estatisticas'}</Text>

          <Text className={'text-xs pb-6 block'}>
            {`Informação relativo a m2 com a nota de que a área habitável é de ${area.toString()} m2`}
          </Text>

          <Field title={'Preço atual por m2'} value={formatCurrency(new BigNumber(paid).dividedBy(area).toFixed(2))} />

          <Field
            title={'Preço Previsto por m2'}
            value={formatCurrency(
              initialPrevision.minus(new BigNumber(budgetDeviation).toFixed(2)).dividedBy(area).toFixed(2)
            )}
          />
        </>
      )}
    </div>
  );
}
